// DocumentTemplateList.js - Mallar → Dokumentmallar (Spår D6): globala mallar + klinikens egna.
// Mallar raderas inte - de inaktiveras (skrivna dokument har en namn-snapshot).
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import apiClient from '../../apiClient';
import '../FormTemplates/FormTemplates.css';

function DocumentTemplateList() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const resp = await apiClient.get('/api/document-templates', { params: { include_inactive: 1 } });
      setTemplates(resp.data.templates || []);
    } catch (err) {
      console.error('Fel vid hämtning av dokumentmallar:', err);
      setError(err.response?.data?.error || 'Kunde inte hämta dokumentmallar');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const globals = templates.filter(t => t.is_global);
  const own = templates.filter(t => !t.is_global);

  const handleCopy = async (template) => {
    const name = window.prompt('Namn på den nya egna mallen:', `${template.name} (kopia)`);
    if (name === null) return;
    try {
      setBusyId(template.template_id);
      const resp = await apiClient.post(`/api/document-templates/${template.template_id}/copy`, { name });
      toast.success(`"${resp.data.name}" skapad som egen mall`);
      navigate(`/dokumentmallar/edit/${resp.data.template_id}`);
    } catch (err) {
      console.error('Kopiering misslyckades:', err);
      toast.error(err.response?.data?.error || 'Kunde inte kopiera mallen');
    } finally {
      setBusyId(null);
    }
  };

  const handleToggleActive = async (template) => {
    try {
      setBusyId(template.template_id);
      await apiClient.put(`/api/document-templates/${template.template_id}`, { is_active: !template.is_active });
      toast.success(template.is_active ? 'Mallen är inaktiverad' : 'Mallen är aktiverad');
      await load();
    } catch (err) {
      console.error('Kunde inte ändra status:', err);
      toast.error(err.response?.data?.error || 'Kunde inte ändra status');
    } finally {
      setBusyId(null);
    }
  };

  const renderCard = (t) => (
    <div key={t.template_id} className={`ft-card ${t.is_active ? '' : 'ft-card-inactive'}`}>
      <div className="ft-card-info">
        <div className="ft-card-title-row">
          <h3 className="ft-card-title">{t.name}</h3>
          {t.is_global && !t.can_edit && <span className="ft-badge ft-badge-global"><i className="fas fa-lock"></i> Global</span>}
          {!t.is_active && <span className="ft-badge ft-badge-inactive">Inaktiv</span>}
        </div>
        <p className="ft-card-meta">
          <span><i className="fas fa-tag"></i> {t.category_label}</span>
          {t.created_by_name && <span><i className="fas fa-user"></i> {t.created_by_name}</span>}
        </p>
      </div>
      <div className="ft-card-actions">
        <button className={`ft-btn ${t.can_edit ? 'ft-btn-primary' : ''}`}
          onClick={() => navigate(`/dokumentmallar/edit/${t.template_id}`)}>
          <i className={`fas ${t.can_edit ? 'fa-edit' : 'fa-eye'}`}></i> {t.can_edit ? 'Redigera' : 'Visa'}
        </button>
        <button className={`ft-btn ${t.can_edit ? '' : 'ft-btn-primary'}`} disabled={busyId === t.template_id}
          onClick={() => handleCopy(t)} title="Skapa en redigerbar kopia för din klinik">
          <i className="fas fa-copy"></i> {t.can_edit ? 'Kopiera' : 'Kopiera som egen'}
        </button>
        {t.can_edit && (
          <button className="ft-btn" disabled={busyId === t.template_id} onClick={() => handleToggleActive(t)}>
            <i className={`fas ${t.is_active ? 'fa-toggle-on' : 'fa-toggle-off'}`}></i>{' '}
            {t.is_active ? 'Inaktivera' : 'Aktivera'}
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="ft-list-container">
      <div className="ft-list-header">
        <div>
          <h2><i className="fas fa-file-signature"></i> Dokumentmallar</h2>
          <p className="ft-list-intro">
            Mallar för dokument som skrivs i patientens journal (intyg, remiss, brev …) under fliken Dokument.
            Globala mallar är gemensamma för alla kliniker och kan inte ändras här — kopiera dem för att anpassa.
            Mallar tas inte bort, de inaktiveras.
          </p>
        </div>
        <button className="ft-btn ft-btn-create" onClick={() => navigate('/dokumentmallar/create')}>
          <i className="fas fa-plus"></i> Skapa egen mall
        </button>
      </div>

      {error && <p className="ft-error"><i className="fas fa-exclamation-circle"></i> {error}</p>}
      {loading && <p className="ft-muted">Hämtar dokumentmallar…</p>}

      {!loading && (
        <>
          <section className="ft-section">
            <h3 className="ft-section-title"><i className="fas fa-globe"></i> Globala mallar</h3>
            {globals.length === 0
              ? <p className="ft-muted">Inga globala mallar finns.</p>
              : globals.map(renderCard)}
          </section>

          <section className="ft-section">
            <h3 className="ft-section-title"><i className="fas fa-hospital"></i> Klinikens mallar</h3>
            {own.length === 0
              ? <p className="ft-muted">Kliniken har inga egna mallar ännu. Skapa en ny eller kopiera en global mall.</p>
              : own.map(renderCard)}
          </section>
        </>
      )}
    </div>
  );
}

export default DocumentTemplateList;
